import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faBoxOpen,
  faLock,
  faShareNodes,
  faWarehouse
} from '@fortawesome/free-solid-svg-icons';

import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import AdvanceTable from '../base/AdvanceTable';
import AdvanceTableFooter from '../base/AdvanceTableFooter';
import useProductAllocation from '../../hooks/useProductAllocation';
import dayjs from 'dayjs';
import { cableLabel } from '../../helpers/cableLabel';
import { destinationBadge } from '../../helpers/orderDestination';
import {
  cableSearchKey,
  matchesSearch,
  productFromKey
} from '../../helpers/cableSearch';
import { clientColour } from '../../helpers/clientColour';
import ProductAllocationModal from './ProductAllocationModal';

/**
 * Every product on the floor, and where its cable can go.
 *
 * The planner's axis. Asked per order, splitting one launch across three orders
 * meant three separate rows whose numbers moved as you worked, and the packing
 * decided which product paid for which promise. Here the product is the row and
 * the choice is yours: click it, and every order that wants this cable is a
 * line with a quantity you set.
 */

const fmt = n => Math.round(Number(n) || 0).toLocaleString();

/** Late in red, this week in amber — the date the decision turns on. */
const dueClass = due => {
  if (!due) return 'text-body-tertiary';
  const days = dayjs(due).startOf('day').diff(dayjs().startOf('day'), 'day');
  if (days <= 0) return 'text-danger fw-bold';
  if (days <= 7) return 'text-warning fw-bold';
  return 'text-body-tertiary';
};

// Colours and labels come from the shared vocabulary; the icons are this
// table's own. Stock used to be grey here — on a screen called "Where can this
// cable go?", the one answer that means "anywhere you like" was the one drawn
// as if it meant nothing.
const ICON = {
  STOCK: faBoxOpen,
  ORDER: faLock,
  MIXED: faShareNodes,
  WAREHOUSE: faWarehouse
};
const DESTINATION = Object.fromEntries(
  Object.entries(ICON).map(([k, icon]) => [
    k,
    { ...destinationBadge(k), icon }
  ])
);

const destinationOf = p => {
  // Left production entirely — every metre received into the warehouse, none
  // promised from here. Without its own state this read as "Stock" with nothing
  // free, which is indistinguishable from a product that promised everything
  // and is the opposite situation.
  if (p.warehouseMeters > 0 && p.freeMeters <= 0 && p.promisedMeters <= 0) {
    return 'WAREHOUSE';
  }
  if (p.promisedMeters <= 0) return 'STOCK';
  if (p.freeMeters <= 0) return 'ORDER';
  return 'MIXED';
};

export const productColumns = [
  {
    id: 'product',
    header: 'Product',
    accessorFn: row => row.label,
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column">
        <span className="fw-semibold">{original.label}</span>
        <a
          href={`/apps/project-management/project-details/${original.jobId}`}
          target="_blank"
          rel="noreferrer"
          className="text-body-tertiary fs-10 text-decoration-none"
          onClick={e => e.stopPropagation()}
        >
          {original.jobCode}
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ms-1" />
        </a>
      </div>
    ),
    meta: { headerProps: { style: { minWidth: 190 } }, cellProps: { className: 'py-2' } }
  },
  {
    id: 'destination',
    header: 'Going to',
    accessorFn: row => DESTINATION[row.destination].label,
    cell: ({ row: { original } }) => {
      const d = DESTINATION[original.destination];
      return (
        <Badge bg={d.bg} variant="phoenix">
          <FontAwesomeIcon icon={d.icon} className="me-1" />
          {d.label}
        </Badge>
      );
    },
    meta: {
      headerProps: { style: { width: '11%' }, className: 'text-center' },
      cellProps: { className: 'text-center py-2' }
    }
  },
  {
    id: 'makes',
    header: 'Makes',
    accessorFn: row => row.plannedLength,
    cell: ({ row: { original } }) => (
      <span className="fw-semibold">{fmt(original.plannedLength)} m</span>
    ),
    meta: {
      headerProps: { style: { width: '11%' }, className: 'text-end' },
      cellProps: { className: 'text-end py-2' }
    }
  },
  {
    id: 'promised',
    header: 'Promised',
    accessorFn: row => row.promisedMeters,
    cell: ({ row: { original } }) =>
      original.promisedMeters > 0 ? (
        <div className="d-flex flex-column align-items-end">
          <span className="text-info fw-semibold">
            {fmt(original.promisedMeters)} m
          </span>
          <span className="text-body-tertiary fs-10 white-space-nowrap">
            {original.promisedTo}
          </span>
        </div>
      ) : (
        <span className="text-body-tertiary">—</span>
      ),
    meta: {
      headerProps: { style: { minWidth: 145 }, className: 'text-end' },
      cellProps: { className: 'text-end py-2' }
    }
  },
  {
    id: 'free',
    header: 'Free to give',
    accessorFn: row => row.freeMeters,
    cell: ({ row: { original } }) => (
      <span
        className={
          original.freeMeters > 0 ? 'text-success fw-bold' : 'text-body-tertiary'
        }
      >
        {fmt(original.freeMeters)} m
      </span>
    ),
    meta: {
      headerProps: { style: { width: '12%' }, className: 'text-end' },
      cellProps: { className: 'text-end py-2' }
    }
  },
  {
    id: 'longest',
    header: 'Longest piece',
    accessorFn: row => row.longestPiece,
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column align-items-end">
        <span
          className={
            original.longestPiece > 0 ? 'fw-semibold' : 'text-body-tertiary'
          }
        >
          {fmt(original.longestPiece)} m
        </span>
        {/* Free metres say how MUCH there is; this says how LONG the biggest
            continuous run is, and that is what decides whether a drum of a
            given length is possible at all. When they differ the cable is in
            pieces, and the difference is the thing to notice. */}
        {original.freeMeters > original.longestPiece && (
          <span className="text-body-tertiary fs-10 white-space-nowrap">
            in {original.pieceCount} pieces
          </span>
        )}
      </div>
    ),
    meta: {
      headerProps: { style: { minWidth: 120 }, className: 'text-end' },
      cellProps: { className: 'text-end py-2' }
    }
  },
  {
    id: 'wanting',
    header: 'Orders waiting',
    accessorFn: row => row.wantingCount,
    cell: ({ row: { original } }) =>
      original.wantingCount > 0 ? (
        <div className="d-flex flex-column align-items-center gap-1">
          {original.waiting.slice(0, 2).map(w => (
            <div key={w.orderCode} className="d-flex align-items-center gap-1">
              <Badge bg={clientColour(w.client)} variant="phoenix" className="fs-10">
                {String(w.client || '—').toUpperCase()}
              </Badge>
              <span className={`fs-10 ${dueClass(w.dueDate)}`}>
                {w.dueDate || 'no date'}
              </span>
            </div>
          ))}
          {original.wantingCount > 2 && (
            <span className="text-body-tertiary fs-10">
              +{original.wantingCount - 2} more
            </span>
          )}
        </div>
      ) : (
        <span className="text-body-tertiary">—</span>
      ),
    meta: {
      headerProps: { style: { minWidth: 140 }, className: 'text-center' },
      cellProps: { className: 'text-center py-2' }
    }
  }
];

const ProductAllocationTable = ({ jobIds = null, lineFilter = null, search = '' }) => {
  const [open, setOpen] = useState(null);
  // Fully promised products are hidden by default — no decision left in them,
  // and on a busy floor they are most of the table. Not removed outright: this
  // is where their promises get taken back, and a row nobody can reach is a
  // promise nobody can undo.
  const [showSpent, setShowSpent] = useState(false);

  // The query and both writes live in the hook — three screens ask this now,
  // and the reservation write is fiddly enough that copies would drift.
  const {
    products,
    isLoading,
    isError,
    busy,
    note,
    error,
    allocate,
    release
  } = useProductAllocation({ jobIds });

  const rows = useMemo(() => {
    return (products || [])
      .map(p => {
        const orders = [...new Set((p.promised || []).map(q => q.orderCode))];
        const waiting = [];
        const seen = new Set();
        for (const c of p.candidates || []) {
          if (!c.items.some(i => i.unitsOpen > 0)) continue;
          if (seen.has(c.orderCode)) continue;
          seen.add(c.orderCode);
          waiting.push({
            orderCode: c.orderCode,
            client: c.client,
            dueDate: c.dueDate
          });
        }
        // Soonest due first — the same order the modal will show them in.
        waiting.sort((a, b) => {
          const av = a.dueDate ? dayjs(a.dueDate).valueOf() : Infinity;
          const bv = b.dueDate ? dayjs(b.dueDate).valueOf() : Infinity;
          return av - bv;
        });
        return {
          ...p,
          label: cableLabel(p.product, p.productLabel || p.productKey),
          destination: destinationOf(p),
          pieceCount: (p.pieces || []).filter(x => x.free > 0).length,
          promisedTo: orders.slice(0, 2).join(', ') +
            (orders.length > 2 ? ` +${orders.length - 2}` : ''),
          waiting,
          wantingCount: waiting.length
        };
      })
      // Narrowed two ways, and either one is enough.
      //
      // A product serves one of the lines the search left standing — which is
      // what "show me this customer's cable" means — OR it answers the search
      // itself. The second matters because a product can exist with no order
      // asking for it: searching "CYYF 3x25+16" should find the drums sitting
      // in stock, not just the orders wanting them.
      .filter(p => {
        if (!lineFilter && !search) return true;
        const servesLine =
          lineFilter &&
          (p.candidates || []).some(c =>
            lineFilter.has(`${c.orderId}::${c.lineId}`)
          );
        const namedItself =
          !!search &&
          matchesSearch(
            cableSearchKey({
              cableName: p.productLabel,
              product: productFromKey(p.productKey)
            }),
            search
          );
        return servesLine || namedItself;
      })
      // Cable that can still be sent somewhere, first.
      .sort((a, b) => b.freeMeters - a.freeMeters);
  }, [products, lineFilter, search]);

  // Cable that has gone to the warehouse is finished business — it is not a
  // product you can allocate from, and it is not "fully promised" either. It
  // belongs on the warehouse screens, not in a list about where cable can go.
  const inWarehouse = rows.filter(r => r.destination === 'WAREHOUSE');
  const onFloor = rows.filter(r => r.destination !== 'WAREHOUSE');

  const spentCount = onFloor.filter(r => r.freeMeters <= 0).length;
  const visible = useMemo(
    () => (showSpent ? onFloor : onFloor.filter(r => r.freeMeters > 0)),
    [onFloor, showSpent]
  );

  const table = useAdvanceTable({
    data: visible,
    columns: productColumns,
    pageSize: 10,
    pagination: true,
    sortable: true
  });

  if (isLoading) {
    return (
      <Card className="mt-3">
        <Card.Body className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" />
          <span className="text-700">Reading what is on the floor…</span>
        </Card.Body>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="mt-3">
        <Card.Body className="text-danger fs-9">
          Could not read the products on the floor.
        </Card.Body>
      </Card>
    );
  }

  const givable = rows.filter(r => r.freeMeters > 0).length;
  // The product the modal is showing, from the LIVE data — so it follows a
  // refetch instead of freezing on whatever was true when it was opened.
  const showing = open ? rows.find(r => r.jobId === open) : null;

  return (
    <>
      <Card className="mt-3">
        <Card.Body>
          <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
            <h3 className="text-1100 mb-0">Where can this cable go?</h3>
            <Badge bg="success" variant="phoenix">
              {givable} with cable to give
            </Badge>
            {spentCount > 0 && (
              <Button
                size="sm"
                variant={showSpent ? 'phoenix-primary' : 'phoenix-secondary'}
                onClick={() => setShowSpent(v => !v)}
              >
                <FontAwesomeIcon icon={faLock} className="me-1" />
                {showSpent ? 'Hide' : 'Show'} {spentCount} fully promised
              </Button>
            )}
            {inWarehouse.length > 0 && (
              <Badge bg="success" variant="phoenix">
                <FontAwesomeIcon icon={faWarehouse} className="me-1" />
                {inWarehouse.length} in the warehouse
              </Badge>
            )}
          </div>
          <p className="text-700 fs-9 mb-3">
            One row per product launched. Click it to choose how much of its
            cable goes to which order — cable is cut, never joined, so what each
            order can have depends on the lengths that exist.
          </p>

          {error && (
            <Alert variant="danger" className="py-2 fs-9">
              {error}
            </Alert>
          )}
          {note && (
            <Alert variant="success" className="py-2 fs-9">
              {note}
            </Alert>
          )}

          {!visible.length && (
            <p className="text-body-tertiary fs-9 mb-3">
              {spentCount > 0
                ? `Every product on the floor is fully promised. Show the ${spentCount} hidden above to take any of it back.`
                : 'Nothing launched yet.'}
            </p>
          )}

          <AdvanceTableProvider {...table}>
            <AdvanceTable
              tableProps={{
                className: 'phoenix-table border-top border-200 fs-9'
              }}
              rowClassName="hover-actions-trigger align-middle"
              getRowProps={row => ({
                style: { cursor: 'pointer' },
                title: 'Choose where this cable goes',
                onClick: () => setOpen(row.original.jobId)
              })}
            />
            <AdvanceTableFooter pagination className="py-3" />
          </AdvanceTableProvider>
        </Card.Body>
      </Card>

      <Modal show={!!showing} onHide={() => setOpen(null)} size="xl" centered scrollable>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fs-7">
            {showing?.label}
            <span className="text-body-tertiary fs-9 ms-2">{showing?.jobCode}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <ProductAllocationModal
            product={showing}
            busy={busy}
            onAllocate={async picks => {
              const ok = await allocate(picks);
              if (ok) setOpen(null);
            }}
            onRelease={promise => release(showing, promise)}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ProductAllocationTable;
